/* Self-contained interactive demo of the FeedKiwi app.
   No Convex/Clerk dependencies — uses local state with mock data.
   Rendered on /demo route and embedded via iframe in the landing page. */

import { useState, useMemo, useEffect } from "react";
import { KiwiIcon } from "@/components/brand/KiwiIcon";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Rss,
  ListVideo,
  Archive,
  PlusCircle,
  BarChart3,
  Settings,
} from "lucide-react";
import { GoalProgressRing } from "@/components/goals/GoalProgressRing";
import { DailyStreakBadge } from "@/components/goals/DailyStreakBadge";
import type { DemoPage, DemoItem, DemoSource } from "./demo/demoData";
import { INITIAL_ITEMS, parseDemoDuration, getSectionForTag } from "./demo/demoData";
import type { MockCitation } from "./demo/DemoDailyDigest";
import { DemoFeedPage } from "./demo/DemoFeedPage";
import { DemoQueuePage } from "./demo/DemoQueuePage";
import { DemoBacklogPage } from "./demo/DemoBacklogPage";
import { DemoSourcesPage } from "./demo/DemoSourcesPage";
import { DemoStatsPage } from "./demo/DemoStatsPage";

const HIGHLIGHT_CLASS = "ring-2 ring-primary/60 rounded-md transition-all duration-300";

/* ── Nav config ── */

const NAV_ITEMS: {
  icon: typeof Rss;
  label: string;
  page: DemoPage;
  showCount?: boolean;
  featureKey?: string;
}[] = [
  { icon: Rss, label: "Feed", page: "feed", showCount: true, featureKey: "feed-nav" },
  { icon: ListVideo, label: "Queue", page: "queue", showCount: true, featureKey: "queue-nav" },
  { icon: Archive, label: "Backlog", page: "backlog", showCount: true, featureKey: "backlog-nav" },
  { icon: PlusCircle, label: "Sources", page: "sources" },
  { icon: BarChart3, label: "Stats", page: "stats" },
];

/* ── Sidebar ── */

function DemoSidebar({
  activePage,
  onNavigate,
  counts,
  highlightedFeature,
  completedMinutes,
  allGoalsMet,
  goals,
}: {
  activePage: DemoPage;
  onNavigate: (page: DemoPage) => void;
  counts: Record<"feed" | "queue" | "backlog", number>;
  highlightedFeature?: string;
  completedMinutes: Record<string, number>;
  allGoalsMet: boolean;
  goals: Record<string, number>;
}) {
  return (
    <aside className="hidden md:flex flex-col w-64 bg-background text-sidebar-foreground h-full border-r border-border shrink-0">
      <div className="px-4 py-4">
        <div className="flex items-center gap-2">
          <KiwiIcon className="size-6 text-primary" />
          <span className="flex-1 text-lg font-bold">FeedKiwi</span>
          <div className={highlightedFeature === "streak" ? HIGHLIGHT_CLASS : ""}>
            <DailyStreakBadge variant="flame" streak={12} active={allGoalsMet} />
          </div>
        </div>
      </div>

      <nav className="flex-1 px-2 space-y-0.5">
        {NAV_ITEMS.map((item, i) => {
          const isActive = activePage === item.page;
          const isHighlighted = item.featureKey === highlightedFeature;
          const count = item.showCount
            ? counts[item.page as keyof typeof counts]
            : undefined;

          return (
            <div key={item.label}>
              <Button
                variant="ghost"
                className={`w-full justify-start gap-2 px-3 ${
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground hover:!bg-sidebar-accent"
                    : "hover:!bg-sidebar-accent/35"
                } ${isHighlighted ? HIGHLIGHT_CLASS : ""}`}
                onClick={() => onNavigate(item.page)}
              >
                <item.icon className="size-4" />
                <span className="flex-1 text-left">{item.label}</span>
                {count !== undefined && (
                  <Badge
                    variant="secondary"
                    className={
                      isActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground"
                        : "bg-transparent text-sidebar-foreground"
                    }
                  >
                    {count}
                  </Badge>
                )}
              </Button>
              {i === 2 && <Separator className="my-2" />}
            </div>
          );
        })}

        <div className="px-1 mt-6">
          <div className={`rounded-lg border border-sidebar-border/60 bg-sidebar-accent/5 px-3 py-3 ${highlightedFeature === "goals" ? HIGHLIGHT_CLASS : ""}`}>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Goals
            </p>
            <div className="grid grid-cols-2 gap-1">
              {Object.entries(goals).map(([tag, target], i) => (
                <GoalProgressRing
                  key={tag}
                  name={getSectionForTag(tag)}
                  completedMinutes={completedMinutes[tag] ?? 0}
                  goalMinutes={target}
                  streak={i === 0 ? 5 : 3}
                />
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className="px-2 pb-4 space-y-1">
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 px-3"
          asChild
        >
          <span>
            <Settings className="size-4" />
            <span className="flex-1 text-left">Settings</span>
          </span>
        </Button>
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
            JD
          </div>
          <span className="flex-1 text-xs text-muted-foreground">Account</span>
          <Badge
            variant="secondary"
            className="text-[10px] h-4 px-1.5 bg-muted text-muted-foreground"
          >
            Pro
          </Badge>
        </div>
      </div>
    </aside>
  );
}

/* ── Main DemoApp ── */

interface DemoAppProps {
  highlightedFeature?: string;
  externalNavigate?: DemoPage | null;
  onNavigationConsumed?: () => void;
  initialItems?: DemoItem[];
  sources?: DemoSource[];
  goals?: Record<string, number>;
}

export function DemoApp({
  highlightedFeature,
  externalNavigate,
  onNavigationConsumed,
  initialItems,
  sources,
  goals,
}: DemoAppProps) {
  const effectiveGoals = useMemo<Record<string, number>>(
    () => goals ?? { tech: 60, science: 30 },
    [goals],
  );
  const effectiveItems = initialItems ?? INITIAL_ITEMS;

  const [activePage, setActivePage] = useState<DemoPage>("queue");
  const [items, setItems] = useState<DemoItem[]>(effectiveItems);
  const [completedMinutes, setCompletedMinutes] = useState<Record<string, number>>(() => {
    const keys = Object.keys(effectiveGoals);
    return keys.length > 0 ? { [keys[0]]: 45 } : {};
  });

  // Reset demo state when occupation changes (initialItems prop changes)
  useEffect(() => {
    setItems(effectiveItems);
    const keys = Object.keys(effectiveGoals);
    setCompletedMinutes(keys.length > 0 ? { [keys[0]]: 45 } : {});
    setActivePage("queue");
  }, [effectiveItems, effectiveGoals]);

  const allGoalsMet = Object.entries(effectiveGoals).every(
    ([tag, target]) => (completedMinutes[tag] ?? 0) >= target,
  );

  useEffect(() => {
    if (externalNavigate) {
      setActivePage(externalNavigate);
      onNavigationConsumed?.();
    }
  }, [externalNavigate, onNavigationConsumed]);

  const counts = useMemo(
    () => ({
      feed: items.filter((i) => i.status === "feed").length,
      queue: items.filter((i) => i.status === "queue").length,
      backlog: items.filter((i) => i.status === "backlog").length,
    }),
    [items],
  );

  function handleQueueItem(id: string) {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: "queue" } : item,
      ),
    );
  }

  function handleCompleteItem(id: string) {
    const item = items.find((i) => i.id === id);
    if (item) {
      const minutes = parseDemoDuration(item.duration);
      const tag = item.tags[0];
      if (tag && (tag in effectiveGoals)) {
        setCompletedMinutes((prev) => ({
          ...prev,
          [tag]: (prev[tag] ?? 0) + minutes,
        }));
      }
    }
    setItems((prev) => prev.filter((item) => item.id !== id));
  }

  function handleDeleteItem(id: string) {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }

  function handleAddDigestItem(citation: MockCitation) {
    const newItem: DemoItem = {
      id: `digest-${citation.id}`,
      title: citation.title,
      source: citation.source,
      duration: citation.duration,
      thumbnail: "https://i.ytimg.com/vi/VIwWgV3Lc6s/mqdefault.jpg",
      contentType: citation.type === "podcast" ? "podcasts" : citation.type === "article" ? "articles" : "videos",
      tags: ["tech"],
      timeAgo: "just now",
      addedAt: 200 + Math.random(),
      status: "queue",
    };
    setItems((prev) => [...prev, newItem]);
  }

  function renderPage() {
    switch (activePage) {
      case "feed":
        return (
          <DemoFeedPage
            items={items}
            onQueueItem={handleQueueItem}
            onDeleteItem={handleDeleteItem}
            onAddDigestItem={handleAddDigestItem}
            highlightedFeature={highlightedFeature}
          />
        );
      case "queue":
        return (
          <DemoQueuePage
            items={items}
            onCompleteItem={handleCompleteItem}
            onDeleteItem={handleDeleteItem}
            highlightedFeature={highlightedFeature}
          />
        );
      case "backlog":
        return (
          <DemoBacklogPage
            items={items}
            onQueueItem={handleQueueItem}
            onCompleteItem={handleCompleteItem}
            highlightedFeature={highlightedFeature}
          />
        );
      case "sources":
        return <DemoSourcesPage sources={sources} />;
      case "stats":
        return <DemoStatsPage />;
    }
  }

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <DemoSidebar
        activePage={activePage}
        onNavigate={setActivePage}
        counts={counts}
        highlightedFeature={highlightedFeature}
        completedMinutes={completedMinutes}
        allGoalsMet={allGoalsMet}
        goals={effectiveGoals}
      />

      <main className="flex-1 flex flex-col h-full w-full overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden flex items-center gap-2 px-4 py-3 border-b border-border">
          <KiwiIcon className="size-5 text-primary" />
          <span className="text-sm font-bold">FeedKiwi</span>
        </div>

        {renderPage()}
      </main>
    </div>
  );
}
